import { useState } from "react";
import { Check, LockKeyhole, Sparkles } from "lucide-react";
import { Sheet } from "@/components/shell/Sheet";
import { toast } from "@/components/shell/Toaster";
import { equipTreasureReward, unequipTreasureSlot } from "@/lib/db/userPrefs";
import type {
  TreasurePrefs,
  TreasureRewardId,
  TreasureSlot,
} from "@/lib/schemas";
import {
  TREASURE_REWARDS,
  treasureRewardById,
  treasureRewardsForSlot,
} from "@/lib/utils/treasure";
import { HeroAvatar } from "./HeroAvatar";
import { TreasureRewardIcon } from "./TreasureRewardIcon";

const SLOT_META: Record<TreasureSlot, { title: string; description: string }> =
  {
    companion: {
      title: "Towarzysz",
      description: "Kto rusza z Tobą na misje?",
    },
    backpack: {
      title: "Plecak",
      description: "Ekwipunek bohatera",
    },
    glasses: {
      title: "Okulary",
      description: "Nowe spojrzenie na zadania",
    },
    hoodie: {
      title: "Bluza",
      description: "Kolory Twojej drużyny",
    },
    necklace: {
      title: "Łańcuch",
      description: "Medalion bohatera",
    },
    hair: {
      title: "Fryzura",
      description: "Charakter od czubka głowy",
    },
    aura: {
      title: "Aura",
      description: "Efekt, który ożywia cały pulpit",
    },
    scene: {
      title: "Tło pokoju",
      description: "Gdzie mieszka Twój bohater?",
    },
  };

const SLOTS: TreasureSlot[] = [
  "companion",
  "backpack",
  "glasses",
  "hoodie",
  "necklace",
  "hair",
  "aura",
  "scene",
];

const DISCOVERY_STEPS = [1, 10, 20, 30, 35];

export function TreasureCollectionSheet({
  open,
  onClose,
  uid,
  treasure,
  previewOnly = false,
  onPreviewEquip,
}: {
  open: boolean;
  onClose: () => void;
  uid: string;
  treasure: TreasurePrefs;
  previewOnly?: boolean;
  onPreviewEquip?: (slot: TreasureSlot, rewardId?: TreasureRewardId) => void;
}) {
  const [busyId, setBusyId] = useState<TreasureRewardId | null>(null);
  const owned = new Set(treasure.ownedRewardIds);
  const ownedCount = owned.size;
  const scene = treasureRewardById(treasure.equipped?.scene);

  const toggleEquip = async (
    rewardId: TreasureRewardId,
    slot: TreasureSlot,
  ) => {
    const isEquipped = treasure.equipped?.[slot] === rewardId;
    if (previewOnly) {
      onPreviewEquip?.(slot, isEquipped ? undefined : rewardId);
      return;
    }

    setBusyId(rewardId);
    try {
      if (isEquipped) await unequipTreasureSlot(uid, slot);
      else await equipTreasureReward(uid, rewardId);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Nie udało się zmienić stroju",
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Garderoba bohatera">
      <div className="pb-3">
        <div
          className="relative overflow-hidden rounded-[24px] border border-[#C9DFBC] bg-[linear-gradient(145deg,#F7F2D7_0%,#DCEFCB_100%)] px-4 pt-3"
          style={
            scene?.scenePath
              ? {
                  backgroundImage: `url("${scene.scenePath}")`,
                  backgroundPosition: "center",
                  backgroundSize: "cover",
                }
              : undefined
          }
        >
          {scene?.scenePath && (
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(255,250,229,0.96)_0%,rgba(255,250,229,0.78)_47%,rgba(255,250,229,0.08)_78%)]" />
          )}
          <div className="absolute left-4 top-4 z-[1] max-w-[44%]">
            <div className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-forest/70">
              Twój bohater
            </div>
            <div className="mt-1 font-display text-[18px] font-extrabold leading-tight text-navy">
              Mieszaj gadżety po swojemu
            </div>
            <p className="mt-1 text-[11px] leading-snug text-slate">
              Możesz założyć po jednym przedmiocie z każdego rzędu.
            </p>
          </div>
          <HeroAvatar
            treasure={treasure}
            size={196}
            className="relative z-[1] ml-auto mr-4"
          />
        </div>

        <section className="mt-3 rounded-[20px] border border-line bg-white p-3.5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.1em] text-forest/70">
                <Sparkles className="h-3.5 w-3.5" />
                Szlak odkrywcy
              </div>
              <div className="mt-0.5 text-[12px] font-semibold text-navy">
                {ownedCount}/{TREASURE_REWARDS.length} gadżetów odkrytych
              </div>
            </div>
            <div className="font-num text-[24px] font-extrabold text-green">
              {Math.round((ownedCount / TREASURE_REWARDS.length) * 100)}%
            </div>
          </div>
          <div className="mt-3 flex items-center">
            {DISCOVERY_STEPS.map((step, index) => {
              const reached = ownedCount >= step;
              return (
                <div
                  key={step}
                  className="flex min-w-0 flex-1 items-center last:flex-none"
                >
                  <span
                    className={`grid h-7 w-7 shrink-0 place-items-center rounded-full border text-[10px] font-extrabold ${
                      reached
                        ? "border-green bg-green text-white"
                        : "border-line bg-cloud text-slate"
                    }`}
                  >
                    {reached ? <Check className="h-3.5 w-3.5" /> : step}
                  </span>
                  {index < DISCOVERY_STEPS.length - 1 && (
                    <span
                      className={`h-1 min-w-3 flex-1 ${
                        ownedCount >= DISCOVERY_STEPS[index + 1]
                          ? "bg-green"
                          : "bg-line"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <p className="mt-3 text-[12px] leading-snug text-slate">
          Skrzynka zawsze daje nowy losowy gadżet. Nic nie przepada, nie ma
          zakupów ani ograniczeń czasowych.
        </p>

        <div className="mt-4 space-y-5">
          {SLOTS.map((slot) => (
            <section key={slot}>
              <div className="mb-2 flex items-end justify-between gap-2">
                <div>
                  <h3 className="font-display text-[16px] font-extrabold text-navy">
                    {SLOT_META[slot].title}
                  </h3>
                  <p className="text-[10px] font-semibold text-slate">
                    {SLOT_META[slot].description}
                  </p>
                </div>
                <span className="text-[10px] font-bold text-forest">
                  {
                    treasureRewardsForSlot(slot).filter((reward) =>
                      owned.has(reward.id),
                    ).length
                  }
                  /{treasureRewardsForSlot(slot).length}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {treasureRewardsForSlot(slot).map((reward) => {
                  const isOwned = owned.has(reward.id);
                  const isEquipped = treasure.equipped?.[slot] === reward.id;
                  return (
                    <button
                      key={reward.id}
                      type="button"
                      disabled={!isOwned || !!busyId}
                      onClick={() => void toggleEquip(reward.id, slot)}
                      className={`relative flex min-h-[126px] flex-col items-center justify-center rounded-[17px] border p-2 text-center transition-transform ${
                        isOwned
                          ? "border-[#C9DFBC] bg-[#F0F8E8] hover:-translate-y-0.5"
                          : "border-line bg-cloud"
                      } ${isEquipped ? "ring-2 ring-green ring-offset-1" : ""}`}
                    >
                      {isEquipped && (
                        <span className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full bg-green text-white">
                          <Check className="h-3 w-3" />
                        </span>
                      )}
                      {isOwned ? (
                        <TreasureRewardIcon
                          reward={reward}
                          className="h-14 w-14"
                        />
                      ) : (
                        <span className="relative grid h-14 w-14 place-items-center overflow-hidden rounded-[16px] bg-white/65">
                          <TreasureRewardIcon
                            reward={reward}
                            className="h-14 w-14 opacity-15 grayscale"
                          />
                          <LockKeyhole className="absolute h-5 w-5 text-slate/70" />
                        </span>
                      )}
                      <span className="mt-1.5 font-display text-[11px] font-extrabold leading-tight text-navy">
                        {isOwned ? reward.title : "Nieodkryty"}
                      </span>
                      <span className="mt-1 text-[9px] font-semibold text-slate">
                        {isEquipped
                          ? "Założony · zdejmij"
                          : isOwned
                            ? "Załóż"
                            : "Czeka w skrzynce"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </Sheet>
  );
}
